// packages/app/src/scaffolder/AzureDevOpsBranchPicker/AzureDevOpsBranchPickerExtension.tsx
import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { FieldExtensionComponentProps } from '@backstage/plugin-scaffolder-react';
import { TextField, CircularProgress, Chip } from '@material-ui/core';
import { Autocomplete } from '@material-ui/lab';
import { makeStyles } from '@material-ui/core/styles';
import { useApi, discoveryApiRef, fetchApiRef } from '@backstage/core-plugin-api';
import { Branch, BranchesResponse } from './types';

const useStyles = makeStyles(theme => ({
  root: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(1),
  },
  branchChip: {
    margin: theme.spacing(0.5),
  },
  masterBranch: {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
  },
}));

/**
 * Componente para seleccionar ramas de Azure DevOps basado en proyecto y repositorio
 */
export const AzureDevOpsBranchPicker = ({
  onChange,
  rawErrors,
  required,
  formData,
  uiSchema,
  formContext,
}: FieldExtensionComponentProps<string>) => {
  const classes = useStyles();
  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);
  
  const [branches, setBranches] = useState<Branch[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [inputValue, setInputValue] = useState('');
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [open, setOpen] = useState(false);

  // Obtener configuración del uiSchema
  const limit = Number(uiSchema?.['ui:options']?.limit) || 50;
  const featureOnly = Boolean(uiSchema?.['ui:options']?.featureOnly);
  
  // Obtener el nombre del proyecto y repositorio desde formContext (valores dinámicos del formulario)
  const projectName = useMemo(() => 
    formContext?.formData?.azureDevOpsProject || 
    uiSchema?.['ui:options']?.projectName || '', 
    [formContext?.formData?.azureDevOpsProject, uiSchema?.['ui:options']?.projectName]
  );
  
  const repositoryName = useMemo(() => 
    formContext?.formData?.azureRepository || 
    uiSchema?.['ui:options']?.repositoryName || '', 
    [formContext?.formData?.azureRepository, uiSchema?.['ui:options']?.repositoryName]
  );

  /**
   * Función para cargar ramas desde el backend
   */
  const fetchBranches = useCallback(
    async (pageNum: number = 1, searchQuery: string = '') => {
      if (!projectName || !repositoryName) {
        setError('Proyecto y repositorio requeridos para cargar ramas');
        return;
      }

      setLoading(true);
      setError(null);

      try {
        // Obtener la URL base del backend usando DiscoveryApi
        const baseUrl = await discoveryApi.getBaseUrl('azuredevops-picker');
        
        const params = new URLSearchParams({
          page: pageNum.toString(),
          limit: limit.toString(),
        });

        if (searchQuery) {
          params.append('search', searchQuery);
        }

        if (featureOnly) {
          params.append('featureOnly', 'true');
        }

        const url = `${baseUrl}/projects/${encodeURIComponent(projectName)}/repositories/${encodeURIComponent(repositoryName)}/branches?${params.toString()}`;
        
        // Usar FetchApi que maneja automáticamente la autenticación
        const response = await fetchApi.fetch(url);
        
        if (!response.ok) {
          throw new Error(`Error al cargar ramas: ${response.status} ${response.statusText}`);
        }

        const data: BranchesResponse = await response.json();
        
        // Si es la primera página, reemplaza; si no, agrega a la lista existente
        if (pageNum === 1) {
          setBranches(data.branches);
        } else {
          setBranches(prev => [...prev, ...data.branches]);
        }

        setHasMore(data.pagination.hasNext);
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Error desconocido';
        setError(errorMessage);
      } finally {
        setLoading(false);
      }
    },
    [discoveryApi, fetchApi, projectName, repositoryName, limit, featureOnly]
  );

  // Limpiar el valor seleccionado cuando cambia el proyecto o repositorio
  useEffect(() => {
    if (formData) {
      onChange(undefined);
    }
  }, [projectName, repositoryName]); // eslint-disable-line react-hooks/exhaustive-deps

  // Cargar ramas cuando cambia el proyecto o repositorio
  useEffect(() => {
    if (projectName && repositoryName) {
      setBranches([]);
      setPage(1);
      fetchBranches(1);
    } else {
      setBranches([]);
      if (!projectName) {
        setError('Selecciona un proyecto primero');
      } else if (!repositoryName) {
        setError('Selecciona un repositorio primero');
      }
    }
  }, [projectName, repositoryName]); // Removido fetchBranches de las dependencias

  // Limpiar inputValue cuando se cierra el dropdown sin selección
  useEffect(() => {
    if (!open && !formData) {
      setInputValue('');
    }
  }, [open, formData]);

  /**
   * Maneja el cambio en el campo de búsqueda
   */
  const handleInputChange = (
    _event: React.ChangeEvent<{}>, 
    value: string, 
    reason: string
  ) => {
    // Siempre actualizar el inputValue para mostrar lo que el usuario está escribiendo
    setInputValue(value);
    
    // Solo realizar búsqueda si es un cambio por input del usuario (no por selección)
    if (reason === 'input' && projectName && repositoryName) {
      setPage(1);
      
      // Realizar búsqueda
      if (value.length > 0) {
        fetchBranches(1, value);
      } else {
        fetchBranches(1);
      }
    }
  };

  /**
   * Maneja el scroll para cargar más ramas (paginación infinita)
   */
  const handleScroll = (event: React.SyntheticEvent) => {
    const listboxNode = event.currentTarget;
    const position = listboxNode.scrollTop + listboxNode.clientHeight;
    
    if (
      listboxNode.scrollHeight - position <= 1 &&
      hasMore &&
      !loading &&
      projectName &&
      repositoryName
    ) {
      const nextPage = page + 1;
      setPage(nextPage);
      fetchBranches(nextPage, inputValue);
    }
  };

  // Convertir branches a opciones (array de strings)
  const options = branches.map(branch => branch.name);

  // Ordenar ramas: master/main primero, luego develop, luego el resto alfabéticamente
  const sortedOptions = [...options].sort((a, b) => {
    const masterBranches = ['master', 'main'];
    const developBranches = ['develop', 'development'];
    
    const aIsMaster = masterBranches.includes(a.toLowerCase());
    const bIsMaster = masterBranches.includes(b.toLowerCase());
    const aIsDevelop = developBranches.includes(a.toLowerCase());
    const bIsDevelop = developBranches.includes(b.toLowerCase());
    
    if (aIsMaster && !bIsMaster) return -1;
    if (!aIsMaster && bIsMaster) return 1;
    if (aIsDevelop && !bIsDevelop && !bIsMaster) return -1;
    if (!aIsDevelop && bIsDevelop && !aIsMaster) return 1;
    
    return a.localeCompare(b);
  });

  return (
    <Autocomplete
        className={classes.root}
        open={open}
        onOpen={() => setOpen(true)}
        onClose={() => setOpen(false)}
        freeSolo={false}
        clearOnBlur={false}
        selectOnFocus={true}
        value={formData || null}
        onChange={(_event, newValue) => {
          onChange(newValue || undefined);
        }}
        inputValue={inputValue}
        onInputChange={handleInputChange}
        options={sortedOptions}
        loading={loading}
        loadingText="Cargando ramas..."
        noOptionsText={
          !projectName 
            ? 'Selecciona un proyecto primero'
            : !repositoryName
              ? 'Selecciona un repositorio primero'
              : error 
                ? `Error: ${error}` 
                : 'No se encontraron ramas'
        }
        disabled={!projectName || !repositoryName}
        getOptionLabel={(option) => option}
        renderInput={params => (
          <TextField
            {...params}
            label="Rama de Azure DevOps"
            required={required}
            error={rawErrors && rawErrors.length > 0}
            helperText={
              rawErrors && rawErrors.length > 0
                ? rawErrors.join(', ')
                : !projectName
                  ? 'Primero selecciona un proyecto'
                  : !repositoryName
                    ? 'Primero selecciona un repositorio'
                    : featureOnly
                      ? 'Busca y selecciona una rama (solo ramas feature/*)'
                      : 'Busca y selecciona una rama'
            }
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {loading ? <CircularProgress color="inherit" size={20} /> : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
        renderOption={(option) => {
          const branch = branches.find(b => b.name === option);
          const isMaster = ['master', 'main'].includes(option.toLowerCase());
          const isDevelop = ['develop', 'development'].includes(option.toLowerCase());
          
          return (
            <div style={{ display: 'flex', alignItems: 'center', width: '100%' }}>
              <span style={{ flexGrow: 1 }}>{option}</span>
              {branch?.isBaseVersion && (
                <Chip 
                  size="small" 
                  label="Base" 
                  className={`${classes.branchChip} ${classes.masterBranch}`}
                />
              )}
              {isMaster && !branch?.isBaseVersion && (
                <Chip 
                  size="small" 
                  label="Master" 
                  color="primary"
                  className={classes.branchChip}
                />
              )}
              {isDevelop && (
                <Chip 
                  size="small" 
                  label="Develop" 
                  color="secondary"
                  className={classes.branchChip}
                />
              )}
            </div>
          );
        }}
        ListboxProps={{
          onScroll: handleScroll,
          style: { maxHeight: '250px' },
        }}
      />
  );
};

// La validación se exporta desde validation.ts
